import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';


interface FullscreenLoaderProps {
  /** Minimum time to show loader (in milliseconds). Defaults to 3000ms */
  minDisplayTime?: number;
  /** Whether the loading is complete */
  isComplete?: boolean;
  /** Callback when fade out animation completes */
  onComplete?: () => void;
  /** Custom message to show below the animation. Defaults to a localised
   *  "Loading…" via `t('common.loading')`. */
  message?: string;
}

const FullscreenLoader = ({
  minDisplayTime = 3000,
  isComplete = false,
  onComplete,
  message,
}: FullscreenLoaderProps) => {
  const { t } = useTranslation();
  const resolvedMessage = message ?? t('common.loading');
  const [showLoader, setShowLoader] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const startRef = useRef(Date.now());

  useEffect(() => {
    if (!isComplete) return;

    const elapsed = Date.now() - startRef.current;
    const remaining = Math.max(minDisplayTime - elapsed, 0);

    const timer = setTimeout(() => {
      setFadeOut(true);
      // Wait for fade animation to complete
      setTimeout(() => {
        setShowLoader(false);
        onComplete?.();
      }, 500); // Match the CSS transition duration
    }, remaining);

    return () => clearTimeout(timer);
  }, [isComplete, minDisplayTime, onComplete]);

  if (!showLoader) return null;

  return (
    <div className="gw-loader" data-fade-out={fadeOut ? 'true' : 'false'}>
      <Loader2 className="gw-loader-icon" aria-hidden />
      <p className="gw-loader-msg">{resolvedMessage}</p>
    </div>
  );
};

export default FullscreenLoader;
